import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import type { SampleRoundListDto } from '@/ApiClient';
import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { entityCache, keyedLruCache, pruneIds } from '@/store/cache-primitives';

export interface SampleRoundListCache {
  filterKey: string;
  orderedIds: number[];
}

// List caches are keyed per filter combination so switching filters back and forth
// restores instantly.
const MAX_LIST_CACHES = 20;

export const useSampleRounds = defineStore('sample-round-cache', () => {
  const entities = entityCache<SampleRoundListDto>();
  const rounds = entities.data;
  const lru = keyedLruCache<SampleRoundListCache>(MAX_LIST_CACHES);
  const listCaches = lru.data;

  // Snapshot persistence contract — see store/warm-boot.ts. The samples board's
  // reloadSampleRounds() always refetches after seeding from these caches, so
  // disk-seeded (possibly stale) lists revalidate on mount without extra machinery.
  const snapshotDto = 'SampleRoundListDto' as const;
  // SampleRoundListCache is a frontend-defined shape the DTO hash can't guard — bump
  // this whenever the snapshot/SampleRoundListCache shape changes.
  const SNAPSHOT_VERSION = 1;

  type SampleRoundsSnapshot = {
    version: number;
    rounds: Record<number, SampleRoundListDto>;
    listCaches: Record<string, SampleRoundListCache>;
  };

  function snapshotData(): SampleRoundsSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    return { version: SNAPSHOT_VERSION, rounds: toRaw(rounds.value), listCaches: toRaw(listCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<SampleRoundsSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(rounds.value).length > 0 || Object.keys(listCaches.value).length > 0) return;
    entities.seed(snapshot.rounds ?? {});
    listCaches.value = snapshot.listCaches ?? {};
  }

  function getOrdered(filterKey: string): SampleRoundListDto[] {
    const cache = lru.get(filterKey);
    if (!cache) return [];
    return cache.orderedIds.map((id) => rounds.value[id]).filter(Boolean);
  }

  function setListState(filterKey: string, ids: number[]) {
    lru.set(filterKey, { filterKey, orderedIds: ids });
  }

  function remove(ids: number[]) {
    entities.remove(ids);
    pruneIds(listCaches, ids);
  }

  function clear() {
    entities.clear();
    lru.clear();
  }

  onTabResume((elapsed) => {
    // Drop only the list caches so the next board mount fetches a fresh list;
    // keep the round DTOs so anything rendered shows data instead of skeletons.
    if (elapsed >= CACHE_TTL) lru.clear();
  });

  return {
    rounds,
    listCaches,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    get: entities.get,
    getListCache: lru.get,
    getOrdered,
    cache: entities.cache,
    setListState,
    remove,
    clear,
  };
});
